import React from 'react';
import {
    Box, Typography, TextField, Button, Slider, Chip,
    FormControlLabel,
    Checkbox, Autocomplete, Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';

const SidebarContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    borderRadius: '22px',
    border: '1px solid #e8e7ef',
    boxShadow: '0 16px 45px rgba(30, 32, 50, 0.06)',
    height: 'fit-content',
    backgroundColor: '#fff',
}));

const FilterSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const FilterTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '0.95rem',
    color: '#343c4f',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const RecipeFilterSidebar = ({
    filters,
    setFilters,
    options,
    onClear
}) => {

    const handleIngredientChange = (event, newValue) => {
        setFilters(prev => ({ ...prev, ingredients: newValue.map(i => i.value) }));
    };

    const handleCategoryChange = (category) => {
        setFilters(prev => ({
            ...prev,
            momentoDelDia: prev.momentoDelDia === category ? '' : category
        }));
    };

    const handlePreferenceToggle = (prefId) => {
        setFilters(prev => {
            const current = prev.restrictions || [];
            const newPrefs = current.includes(prefId)
                ? current.filter(id => id !== prefId)
                : [...current, prefId];
            return { ...prev, restrictions: newPrefs };
        });
    };

    return (
        <SidebarContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="800" color="#334155">
                    Filtros
                </Typography>
                <Button
                    size="small"
                    onClick={onClear}
                    sx={{ textTransform: 'none', color: '#64748b' }}
                >
                    Limpiar todo
                </Button>
            </Box>

            {/* 1. Ingredientes */}
            <FilterSection>
                <FilterTitle>🥕 Ingredientes</FilterTitle>
                <Autocomplete
                    multiple
                    options={options.ingredients || []}
                    getOptionLabel={(option) => option.label || 'Unknown'}
                    value={options.ingredients.filter(i => filters.ingredients.includes(i.value))}
                    onChange={handleIngredientChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Pollo, nueces..."
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    bgcolor: '#f8fafc',
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                                    '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                }
                            }}
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option.label}
                                {...getTagProps({ index })}
                                size="small"
                                sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 600 }}
                            />
                        ))
                    }
                />
            </FilterSection>

            {/* 2. Momento (was Categorías) */}
            <FilterSection>
                <FilterTitle>🍱 Momento</FilterTitle>
                <Box display="flex" flexWrap="wrap" gap={1}>
                    {['Desayuno', 'Comida', 'Cena', 'Snack'].map(cat => (
                        <Chip
                            key={cat}
                            label={cat}
                            onClick={() => handleCategoryChange(cat)}
                            variant={filters.momentoDelDia === cat ? 'filled' : 'outlined'}
                            sx={{
                                borderRadius: '12px',
                                fontWeight: 600,
                                bgcolor: filters.momentoDelDia === cat ? '#e0e7ff' : 'transparent',
                                color: filters.momentoDelDia === cat ? '#4338ca' : '#64748b',
                                border: '1px solid',
                                borderColor: filters.momentoDelDia === cat ? '#e0e7ff' : '#e2e8f0',
                                '&:hover': { bgcolor: '#e0e7ff', borderColor: '#e0e7ff' }
                            }}
                        />
                    ))}
                </Box>
            </FilterSection>

            {/* 3. Dificultad */}
            <FilterSection>
                <FilterTitle>📊 Dificultad</FilterTitle>
                <Box display="flex" gap={1} mb={1}>
                    {['Baja', 'Media', 'Alta'].map(diff => (
                        <Button
                            key={diff}
                            fullWidth
                            variant="outlined"
                            onClick={() => setFilters(prev => ({ ...prev, difficulty: prev.difficulty === diff ? '' : diff }))}
                            sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                py: 1,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                borderColor: filters.difficulty === diff ? '#334155' : '#e2e8f0',
                                color: filters.difficulty === diff ? '#fff' : '#64748b',
                                bgcolor: filters.difficulty === diff ? '#334155' : 'transparent',
                                '&:hover': {
                                    bgcolor: filters.difficulty === diff ? '#1e293b' : '#f8fafc',
                                    borderColor: filters.difficulty === diff ? '#1e293b' : '#cbd5e1'
                                }
                            }}
                        >
                            {diff === 'Baja' ? 'Fácil' : diff === 'Alta' ? 'Difícil' : diff}
                        </Button>
                    ))}
                </Box>
                {/* Compact Estado Filter */}
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={filters.onlyReviewed || false}
                            onChange={(e) => setFilters(prev => ({ ...prev, onlyReviewed: e.target.checked }))}
                            color="primary"
                            sx={{ padding: '4px 8px 4px 0' }}
                        />
                    }
                    label={<Typography variant="body2" color="#64748b">Solo recetas revisadas</Typography>}
                    sx={{ marginLeft: 0, marginTop: 0.5 }}
                />
            </FilterSection>

            {/* 4. Tiempo */}
            <FilterSection>
                <FilterTitle>⏱️ Tiempo Prep.</FilterTitle>
                <Slider
                    value={filters.maxTime || 120}
                    onChange={(e, val) => setFilters(prev => ({ ...prev, maxTime: val }))}
                    valueLabelDisplay="auto"
                    step={5}
                    marks={[
                        { value: 5, label: '5m' },
                        { value: 60, label: '60m' },
                        { value: 120, label: '120+' },
                    ]}
                    min={5}
                    max={120}
                    sx={{
                        color: '#667eea',
                        '& .MuiSlider-thumb': {
                            boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
                        }
                    }}
                />
            </FilterSection>

            {/* 5. Categoría Receta */}
            <FilterSection>
                <FilterTitle>📑 Categoría</FilterTitle>
                <Autocomplete
                    options={options.categories || []}
                    getOptionLabel={(option) => option.nombre || 'Unknown'}
                    value={options.categories?.find(c => c._id === filters.recipeCategory) || null}
                    onChange={(e, val) => setFilters(prev => ({ ...prev, recipeCategory: val ? val._id : '' }))}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Seleccionar..."
                            variant="outlined"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    bgcolor: '#f8fafc',
                                    '& fieldset': { borderColor: '#e2e8f0' },
                                }
                            }}
                        />
                    )}
                />
            </FilterSection>

            {/* 6. Tipo de Cocina */}
            <FilterSection>
                <FilterTitle>🌍 Tipo de Cocina</FilterTitle>
                <Box display="flex" flexWrap="wrap" gap={1}>
                    {(options.types || []).map(type => (
                        <Chip
                            key={type._id}
                            label={type.name}
                            onClick={() => setFilters(prev => ({ ...prev, cookingType: prev.cookingType === type._id ? '' : type._id }))}
                            variant={filters.cookingType === type._id ? 'filled' : 'outlined'}
                            sx={{
                                borderRadius: '12px',
                                fontWeight: 600,
                                bgcolor: filters.cookingType === type._id ? '#fef3c7' : 'transparent',
                                color: filters.cookingType === type._id ? '#b45309' : '#64748b',
                                border: '1px solid',
                                borderColor: filters.cookingType === type._id ? '#fcd34d' : '#e2e8f0',
                                '&:hover': { bgcolor: '#fef3c7', borderColor: '#fcd34d' }
                            }}
                        />
                    ))}
                </Box>
            </FilterSection>

            {/* 7. Tipo de Plato */}
            <FilterSection>
                <FilterTitle>🍽️ Tipo de Plato</FilterTitle>
                <Box display="flex" flexWrap="wrap" gap={1}>
                    {(options.dishTypes || []).map(type => (
                        <Chip
                            key={type}
                            label={type}
                            onClick={() => setFilters(prev => ({ ...prev, dishType: prev.dishType === type ? '' : type }))}
                            variant={filters.dishType === type ? 'filled' : 'outlined'}
                            sx={{
                                borderRadius: '12px',
                                fontWeight: 600,
                                bgcolor: filters.dishType === type ? '#e0e7ff' : 'transparent',
                                color: filters.dishType === type ? '#4338ca' : '#64748b',
                                border: '1px solid',
                                borderColor: filters.dishType === type ? '#e0e7ff' : '#e2e8f0',
                                '&:hover': { bgcolor: '#e0e7ff', borderColor: '#e0e7ff' }
                            }}
                        />
                    ))}
                </Box>
            </FilterSection>

            {/* Preferencias (Restricciones) - Kept at end */}
            <FilterSection>
                <FilterTitle>🌱 Preferencias</FilterTitle>
                <Box display="flex" flexWrap="wrap" gap={1}>
                    {options.restrictions.map(pref => (
                        <Chip
                            key={pref._id}
                            label={pref.name}
                            onClick={() => handlePreferenceToggle(pref._id)}
                            variant={filters.restrictions.includes(pref._id) ? 'filled' : 'outlined'}
                            sx={{
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                bgcolor: filters.restrictions.includes(pref._id) ? '#dcfce7' : 'transparent',
                                color: filters.restrictions.includes(pref._id) ? '#166534' : '#64748b',
                                border: '1px solid',
                                borderColor: filters.restrictions.includes(pref._id) ? '#dcfce7' : '#e2e8f0',
                                '&:hover': { bgcolor: '#dcfce7', borderColor: '#dcfce7' }
                            }}
                        />
                    ))}
                </Box>
            </FilterSection>



        </SidebarContainer>
    );
};

export default RecipeFilterSidebar;
